/**
 * Mini-visualizaciones de los KPIs (Claude Design · Dashboard.dc.html).
 *
 * TODAS se alimentan de la serie mensual real que devuelve
 * /dashboard/charts (MonthlyChartData: invoices, amount, approved, pending,
 * rejected por mes). Ninguna genera datos.
 *
 * Si la serie viene vacia, cada componente devuelve null en vez de dibujar una
 * linea plana: un grafico vacio se lee como "no hubo actividad", que no es lo
 * mismo que "no hay datos".
 */

interface SeriesProps {
  /** Valores en orden cronologico. */
  values: number[];
  color: string;
  /** Id unico para el gradiente del area (los ids son globales en SVG). */
  gradientId: string;
  className?: string;
}

const W = 160;
const H = 32;

/** Normaliza la serie al alto del SVG dejando 2px de aire arriba y abajo. */
function toPoints(values: number[]): string[] {
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const step = values.length > 1 ? W / (values.length - 1) : W;
  return values.map((v, i) => {
    const x = i * step;
    const y = H - 2 - ((v - min) / span) * (H - 4);
    return `${x.toFixed(1)} ${y.toFixed(1)}`;
  });
}

/** Linea + area, para series de tendencia. */
export function Sparkline({ values, color, gradientId, className }: SeriesProps) {
  if (values.length < 2) return null;

  const pts = toPoints(values);
  const line = `M${pts.join(' L')}`;
  const area = `${line} L${W} ${H} L0 ${H} Z`;
  const last = pts[pts.length - 1].split(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={`block h-8 w-full ${className ?? ''}`} aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={color} />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} opacity=".18" />
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.5" fill={color} />
    </svg>
  );
}

/** Barras, para series de magnitud. La ultima se destaca. */
export function MiniBars({
  values,
  color,
  // Violeta claro en vez de gris: el gris leia como dato apagado o vacio.
  mutedColor = '#d7c2ea',
}: {
  values: number[];
  color: string;
  mutedColor?: string;
}) {
  if (values.length === 0) return null;

  // Escala relativa min→max (como un sparkline), no desde cero: con montos de
  // 168k a 245k en 32px de alto, escalar desde cero deja todas las barras
  // practicamente iguales y la tendencia no se lee.
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const FLOOR = 28; // % de alto del mes mas bajo

  return (
    <div className="flex h-8 items-end gap-1">
      {values.map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm"
          style={{
            height: `${FLOOR + ((v - min) / span) * (100 - FLOOR)}%`,
            background: i === values.length - 1 ? color : mutedColor,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Dos lineas sobre la misma escala, para comparar dos series mensuales (p.ej.
 * Aprobadas vs Rechazadas) sin gastar una consulta aparte — ambas ya vienen
 * en la misma serie mensual que alimenta Sparkline/MiniBars.
 */
export function DualSparkline({
  valuesA,
  valuesB,
  colorA,
  colorB,
  className,
}: {
  valuesA: number[];
  valuesB: number[];
  colorA: string;
  colorB: string;
  className?: string;
}) {
  if (valuesA.length < 2 || valuesB.length < 2) return null;

  const all = [...valuesA, ...valuesB];
  const max = Math.max(...all);
  const min = Math.min(...all);
  const span = max - min || 1;
  const step = valuesA.length > 1 ? W / (valuesA.length - 1) : W;

  const toPts = (values: number[]) =>
    values.map((v, i) => {
      const x = i * step;
      const y = H - 2 - ((v - min) / span) * (H - 4);
      return `${x.toFixed(1)} ${y.toFixed(1)}`;
    });

  const ptsA = toPts(valuesA);
  const ptsB = toPts(valuesB);
  const lastA = ptsA[ptsA.length - 1].split(' ');
  const lastB = ptsB[ptsB.length - 1].split(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={`block h-8 w-full ${className ?? ''}`} aria-hidden="true">
      <path d={`M${ptsA.join(' L')}`} fill="none" stroke={colorA} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastA[0]} cy={lastA[1]} r="2.5" fill={colorA} />
      <path d={`M${ptsB.join(' L')}`} fill="none" stroke={colorB} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastB[0]} cy={lastB[1]} r="2.5" fill={colorB} />
    </svg>
  );
}

/**
 * Barra de proporcion: que parte del total representa `value`. Se usa cuando
 * hay un conteo y un total reales pero no una serie temporal.
 */
export function ShareBar({
  value,
  total,
  color,
}: {
  value: number;
  total: number;
  color: string;
}) {
  if (total <= 0) return null;
  const pct = Math.min(100, Math.max(0, (value / total) * 100));
  return (
    <div className="h-1 overflow-hidden rounded-sm bg-[#eef2f6] dark:bg-secondary-700">
      <div className="h-full rounded-sm transition-[width]" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

/**
 * Historico + tramo proyectado en linea punteada. El ultimo punto real y la
 * proyeccion salen de campos distintos de la API (serie mensual y
 * projected_revenue), por eso se dibujan con trazos distintos.
 */
export function ProjectionLine({
  values,
  projected,
  color,
  todayLabel,
  endLabel,
}: {
  values: number[];
  projected: number;
  color: string;
  todayLabel: string;
  endLabel: string;
}) {
  if (values.length < 2) return null;

  const all = [...values, projected];
  const max = Math.max(...all);
  const min = Math.min(...all);
  const span = max - min || 1;
  const width = 240;
  const height = 44;
  const step = width / all.length;

  const xy = (v: number, i: number) => {
    const x = i * step + step / 2;
    const y = height - 4 - ((v - min) / span) * (height - 12);
    return [x, y] as const;
  };

  const histPts = values.map((v, i) => xy(v, i));
  const [lastX, lastY] = histPts[histPts.length - 1];
  const [projX, projY] = xy(projected, all.length - 1);

  return (
    <svg viewBox={`0 0 ${width} ${height + 14}`} className="mt-3 block h-14 w-full" aria-hidden="true">
      <path
        d={`M${histPts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L')}`}
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d={`M${lastX.toFixed(1)} ${lastY.toFixed(1)} L${projX.toFixed(1)} ${projY.toFixed(1)}`}
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeDasharray="3 3"
      />
      <circle cx={lastX} cy={lastY} r="3" fill={color} />
      <circle cx={projX} cy={projY} r="3" fill={color} />
      <text x={lastX} y={height + 10} textAnchor="middle" fontSize="8" fill="#a1a1aa" className="font-mono">
        {todayLabel}
      </text>
      <text x={width} y={height + 10} textAnchor="end" fontSize="8" fill="#a1a1aa" className="font-mono">
        {endLabel}
      </text>
    </svg>
  );
}
